import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Code2,
  Palette,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";

interface CompanyOption {
  id: string;
  slug: string;
  name: string;
}

interface CompanyServices {
  company: {
    id: string;
    name: string;
    primary_color: string;
    tracking_page_url: string | null;
    services: Array<{ id: string; label: string; icon?: string }>;
  };
}

export function NewWidget() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: () => api<{ items: CompanyOption[] }>("/master/companies"),
  });

  // Pre-select a company if the caller passed ?company=:id (e.g. clicking
  // the inline "Widget hinzufügen" button on the widgets list).
  const initialCompany = searchParams.get("company") ?? "";
  const [companyId, setCompanyId] = useState<string>(initialCompany);
  const [name, setName] = useState("");
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [allServices, setAllServices] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("");
  const [trackingPageUrl, setTrackingPageUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Load the chosen company's full service list so we can offer checkboxes.
  const slug = useMemo(
    () => companies?.items.find((c) => c.id === companyId)?.slug,
    [companies, companyId],
  );
  const { data: companyDetail } = useQuery<CompanyServices>({
    queryKey: ["company-services", slug],
    queryFn: () => api(`/master/companies/${slug}`),
    enabled: !!slug,
  });

  // Default new widget to include all of the company's services.
  useEffect(() => {
    if (companyDetail?.company.services) {
      setServiceIds(companyDetail.company.services.map((s) => s.id));
      setAllServices(true);
    }
  }, [companyDetail]);

  const createWidget = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/master/widgets", {
        method: "POST",
        body: {
          companyId,
          name,
          serviceIds: allServices ? [] : serviceIds,
          primaryColor: primaryColor || null,
          trackingPageUrl: trackingPageUrl || null,
        },
      }),
    onSuccess: (data) => navigate(`/master/widgets/${data.id}`),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Fehler"),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createWidget.mutate();
  }

  function toggle(id: string) {
    setServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setAllServices(false);
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/master/widgets"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Widgets
      </Link>
      <h1 className="page-title">Neues Widget</h1>

      <section className="card">
        <h2 className="section-title mb-4">
          <Code2 className="h-3.5 w-3.5 text-slate-400" /> Grunddaten
        </h2>
        <div className="space-y-3">
          <Row label="Unternehmen">
            <select
              className="input"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              required
            >
              <option value="">Wählen…</option>
              {companies?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Bezeichnung">
            <input
              className="input"
              placeholder="z.B. Hauptseite oder Solar-Landingpage"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Row>
        </div>
      </section>

      {companyDetail && (
        <section className="card">
          <h2 className="section-title mb-4">
            <Building2 className="h-3.5 w-3.5 text-slate-400" /> Services
          </h2>
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
              checked={allServices}
              onChange={(e) => {
                setAllServices(e.target.checked);
                if (e.target.checked && companyDetail) {
                  setServiceIds(
                    companyDetail.company.services.map((s) => s.id),
                  );
                }
              }}
            />
            <span className="font-medium">Alle Services anzeigen</span>
          </label>

          {!allServices && (
            <div className="space-y-2">
              {companyDetail.company.services.map((s) => (
                <label
                  key={s.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition ${
                    serviceIds.includes(s.id)
                      ? "border-brand bg-brand/5"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
                    checked={serviceIds.includes(s.id)}
                    onChange={() => toggle(s.id)}
                  />
                  <span className="text-sm font-medium text-slate-800">
                    {s.label}
                  </span>
                </label>
              ))}
              <p className="text-xs text-slate-500">
                {serviceIds.length} von{" "}
                {companyDetail.company.services.length} ausgewählt
              </p>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <h2 className="section-title mb-4">
          <Palette className="h-3.5 w-3.5 text-slate-400" /> Optionale
          Anpassungen
        </h2>
        <div className="space-y-3">
          <Row label="Farbe überschreiben">
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                value={
                  primaryColor ||
                  companyDetail?.company.primary_color ||
                  "#1a1a2e"
                }
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
              <span className="font-mono text-sm text-slate-600">
                {primaryColor || "Standard"}
              </span>
              {primaryColor && (
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:underline"
                  onClick={() => setPrimaryColor("")}
                >
                  zurücksetzen
                </button>
              )}
            </div>
          </Row>
          <Row label="Tracking-Seite überschreiben">
            <input
              className="input"
              placeholder={
                companyDetail?.company.tracking_page_url ?? "Standard verwenden"
              }
              value={trackingPageUrl}
              onChange={(e) => setTrackingPageUrl(e.target.value)}
            />
          </Row>
        </div>
      </section>

      {error && (
        <p className="inline-flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary"
          disabled={!companyId || !name || createWidget.isPending}
        >
          Widget erstellen
        </button>
      </div>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <div>{children}</div>
    </label>
  );
}
